import {
  PriceService,
  priceServiceKeys,
} from 'api/services/httpServices/PriceService';
import Big from 'big.js';
import { duration } from 'moment';
import { useQuery } from '@tanstack/react-query';
import { Chains, gasPrioritySuffix } from 'api/types/httpsTypes/d-wallets';
import { ChainInfoProps, useChainInfo } from 'api/hooks/useChainInfo';
import { fromAmount, parseAmount } from '../../app/dex/components/chains/units';
import { formatTokenAmount } from 'utils/FormatNumber';
export function GasEstimation({
  priorityFee = 0,
  bribe = 0,
  chain,
}: {
  priorityFee?: number;
  bribe?: number;
  chain: Chains;
}) {
  const chainInfo = useChainInfo(chain);
  const price = chainInfo?.price ?? 0;
  const estimatedGasAmountGwei = Number(useEstimatedGasAmountGwei() || 0);

  const safePriorityFee = Number.isFinite(priorityFee) ? priorityFee : 0;
  const safeBribe = Number.isFinite(bribe) ? bribe : 0;

  if (!chainInfo || !price) {
    return <></>;
  }

  const suffix = gasPrioritySuffix(chain);

  const gasContent = (value: number, usdValue: string) => (
    <div className="dark:bg-dex-black-700 xxs:px-2 xxs:py-1 lg:mt-auto flex justify-between rounded-sm">
      <div className="xxs:text-sm lg:text-xs xxs:text-dex-white-secondary">
        Estimated Gas
      </div>
      <div className="xxs:text-sm lg:text-xs xxs:font-semibold">
        {formatTokenAmount(value)} {suffix} (${usdValue})
      </div>
    </div>
  );

  if (chain === Chains.Solana) {
    const gasFee = Big(safePriorityFee).plus(safeBribe);
    const gasValueUSD = gasFee.times(price).toFixed(2);
    return gasContent(Number(gasFee), gasValueUSD);
  }

  const baseFeeInGwei = fromAmount(chainInfo.gasPrice ?? 0, chain, 'weiToGwei');
  const bribeInGwei = fromAmount(
    parseAmount(safeBribe, chain, 'etherToWei'),
    chain,
    'weiToGwei'
  );

  const totalGasFeeInGwei = Big(safePriorityFee)
    .plus(baseFeeInGwei)
    .times(estimatedGasAmountGwei)
    .plus(bribeInGwei);

  const totalGasInEth = fromAmount(
    parseAmount(Number(totalGasFeeInGwei), chain, 'gweiToWei'),
    chain,
    'weiToEther'
  );

  const gasValueUSD = Big(totalGasInEth).times(price).toFixed(2);

  return gasContent(Number(totalGasFeeInGwei), gasValueUSD);
}

export function useCalculateGasCostInEther(
  chainInfo: ChainInfoProps | null,
  priorityFee: number,
  chain: Chains,
  bribe: number
) {
  const estimatedGasAmountGwei = useEstimatedGasAmountGwei();

  const safePriorityFee = Number.isFinite(priorityFee) ? priorityFee : 0;
  const safeBribe = Number.isFinite(bribe) ? bribe : 0;

  if (!chainInfo || !chain) {
    return Big(0);
  }

  if (chain === Chains.Solana) {
    return Number(Big(safePriorityFee).plus(safeBribe));
  }

  const baseFeeInGwei = fromAmount(chainInfo.gasPrice ?? 0, chain, 'weiToGwei');
  const bribeInGwei = fromAmount(
    parseAmount(safeBribe, chain, 'etherToWei'),
    chain,
    'weiToGwei'
  );

  const totalGasFeeInGwei = Big(safePriorityFee)
    .plus(baseFeeInGwei)
    .times(estimatedGasAmountGwei)
    .plus(bribeInGwei);

  return fromAmount(
    parseAmount(Number(totalGasFeeInGwei ?? 0), chain, 'gweiToWei'),
    chain,
    'weiToEther'
  );
}

export function useEstimatedGasAmountGwei() {
  const { data } = useQuery({
    queryKey: priceServiceKeys.getGasAmount(),
    queryFn: PriceService.getGasAmount,
    refetchInterval: duration('1', 'minute').asMilliseconds(),
  });
  return data ?? 0;
}
